import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDashboard } from '../../contexts/DashboardContext';
import { MAX_DASHBOARDS } from '../../types/dashboard';
import { PortalDropdown } from '../PortalDropdown';

/**
 * Open layouts as tabs. The library (`dashboards`) is larger than the tab
 * strip: × closes a tab without destroying the saved layout; + reopens a
 * closed one or creates a new one. Permanent delete lives in Manage.
 */
export const DashboardTabs: React.FC = () => {
  const {
    dashboards,
    openDashboards,
    closedDashboards,
    currentDashboardId,
    switchDashboard,
    openDashboard,
    closeDashboard,
    createDashboard,
    renameDashboard,
    setDefaultDashboard,
    deleteDashboard,
  } = useDashboard();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [menu, setMenu] = useState<{ id: number; x: number; y: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const manageRef = useRef<HTMLButtonElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setOverflow({
      left: el.scrollLeft > 1,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    updateOverflow();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [openDashboards.length, updateOverflow]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [currentDashboardId]);

  const scrollByPage = (dir: -1 | 1): void => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.7), behavior: 'smooth' });
  };

  const scrollable = overflow.left || overflow.right;

  const maskGradient =
    overflow.left && overflow.right
      ? 'linear-gradient(to right, transparent, #000 24px, #000 calc(100% - 24px), transparent)'
      : overflow.left
        ? 'linear-gradient(to right, transparent, #000 24px)'
        : overflow.right
          ? 'linear-gradient(to right, #000 calc(100% - 24px), transparent)'
          : undefined;

  const canCloseTab = openDashboards.length > 1;
  const canDeleteSaved = dashboards.length > 1;
  const canCreate = dashboards.length < MAX_DASHBOARDS;
  const pendingDelete = dashboards.find((d) => d.id === confirmId) ?? null;
  const menuDashboard = menu ? dashboards.find((d) => d.id === menu.id) ?? null : null;

  useEffect(() => {
    if (!menu) return;
    const close = (): void => setMenu(null);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const beginRename = (id: number, name: string): void => {
    setEditingId(id);
    setDraft(name);
    setMenu(null);
  };

  const commitRename = (): void => {
    if (editingId === null) return;
    const trimmed = draft.trim();
    if (trimmed) renameDashboard(editingId, trimmed);
    setEditingId(null);
  };

  const handleCreate = (): void => {
    if (!canCreate) return;
    const id = createDashboard();
    if (id === null) return;
    beginRename(id, `Dashboard #${id}`);
    setAddOpen(false);
    setManageOpen(false);
  };

  const handleOpenSaved = (id: number): void => {
    openDashboard(id);
    setAddOpen(false);
    setManageOpen(false);
  };

  const handleRenameFromMenu = (id: number, name: string): void => {
    openDashboard(id);
    setManageOpen(false);
    setAddOpen(false);
    beginRename(id, name);
  };

  return (
    <div className="flex items-center gap-1 min-w-0">
      {scrollable && (
        <button
          onClick={() => scrollByPage(-1)}
          disabled={!overflow.left}
          title="Scroll tabs left"
          aria-label="Scroll tabs left"
          className="flex-shrink-0 w-5 h-6 flex items-center justify-center rounded text-gray-500 hover:text-white hover:bg-dark-surface disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-500 transition-colors"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <div
        ref={scrollRef}
        onScroll={updateOverflow}
        onWheel={(e) => {
          const el = scrollRef.current;
          if (!el || el.scrollWidth <= el.clientWidth) return;
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            el.scrollLeft += e.deltaY;
          }
        }}
        className="flex items-center gap-1 min-w-0 overflow-x-auto scrollbar-hide"
        style={{
          maskImage: maskGradient,
          WebkitMaskImage: maskGradient,
        }}
      >
      {openDashboards.map((d) => {
        const isActive = d.id === currentDashboardId;

        if (isActive && editingId === d.id && !manageOpen) {
          return (
            <input
              key={d.id}
              autoFocus
              value={draft}
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setEditingId(null);
              }}
              className="w-28 px-2 py-1 text-sm bg-dark-bg border border-accent-yellow rounded text-white focus:outline-none"
            />
          );
        }

        return (
          <div
            key={d.id}
            ref={isActive ? activeRef : undefined}
            className={`group flex-shrink-0 flex items-center border-b-2 transition-colors ${
              isActive ? 'border-accent-yellow' : 'border-transparent'
            }`}
          >
            <button
              onClick={() => (isActive ? beginRename(d.id, d.name) : switchDashboard(d.id))}
              onDoubleClick={(e) => {
                e.preventDefault();
                if (!isActive) switchDashboard(d.id);
                beginRename(d.id, d.name);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setMenu({ id: d.id, x: e.clientX, y: e.clientY });
              }}
              onAuxClick={(e) => {
                if (e.button === 1 && canCloseTab) closeDashboard(d.id);
              }}
              title={isActive ? 'Click or double-click to rename' : `Switch to ${d.name}`}
              className={`pl-3 py-1 text-sm font-semibold whitespace-nowrap transition-colors ${
                isActive
                  ? 'text-white cursor-text'
                  : 'text-white/55 hover:text-white'
              } ${canCloseTab ? 'pr-1' : 'pr-3'}`}
            >
              {d.name}
            </button>

            {canCloseTab && (
              <button
                onClick={() => closeDashboard(d.id)}
                title={`Close ${d.name} (keeps the saved layout)`}
                aria-label={`Close ${d.name}`}
                className={`mr-2 w-4 h-4 flex items-center justify-center rounded text-gray-500 hover:text-white hover:bg-dark-surface-light transition-all ${
                  isActive ? 'opacity-70' : 'opacity-0 group-hover:opacity-70'
                } hover:!opacity-100`}
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        );
      })}

      </div>

      {scrollable && (
        <button
          onClick={() => scrollByPage(1)}
          disabled={!overflow.right}
          title="Scroll tabs right"
          aria-label="Scroll tabs right"
          className="flex-shrink-0 w-5 h-6 flex items-center justify-center rounded text-gray-500 hover:text-white hover:bg-dark-surface disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-500 transition-colors"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <button
        ref={addRef}
        onClick={() => {
          setManageOpen(false);
          setMenu(null);
          setAddOpen((open) => !open);
        }}
        title="Open or create a layout"
        aria-label="Open or create a layout"
        aria-expanded={addOpen}
        className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded border border-dashed transition-colors ${
          addOpen
            ? 'border-accent-yellow text-accent-yellow'
            : 'border-dark-border text-gray-500 hover:text-accent-yellow hover:border-accent-yellow'
        }`}
      >
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M12 5v14M5 12h14" strokeLinecap="round" />
        </svg>
      </button>

      <button
        ref={manageRef}
        onClick={() => {
          setAddOpen(false);
          setMenu(null);
          setManageOpen((open) => !open);
        }}
        title="Manage layouts"
        aria-label="Manage layouts"
        aria-expanded={manageOpen}
        className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded border transition-colors ${
          manageOpen
            ? 'border-accent-yellow text-accent-yellow bg-dark-bg'
            : 'border-dark-border text-gray-500 hover:text-white hover:border-gray-400'
        }`}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.75" />
          <circle cx="12" cy="12" r="1.75" />
          <circle cx="12" cy="19" r="1.75" />
        </svg>
      </button>

      <PortalDropdown
        anchorRef={addRef}
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        width={260}
        align="left"
      >
        <div className="py-1 max-h-96 overflow-y-auto">
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[1.5px] text-gray-500">
            Open a layout
          </div>
          <button
            onClick={handleCreate}
            disabled={!canCreate}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-200 hover:bg-dark-surface-light transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {canCreate ? 'New layout' : `Maximum of ${MAX_DASHBOARDS} saved`}
          </button>

          {closedDashboards.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[1.5px] text-gray-500 border-t border-dark-border mt-1">
                Saved
              </div>
              {closedDashboards.map((dashboard) => (
                <button
                  key={dashboard.id}
                  onClick={() => handleOpenSaved(dashboard.id)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-gray-100 hover:bg-dark-surface-light transition-colors text-left"
                >
                  <span className="truncate">{dashboard.name}</span>
                  <span className="flex-shrink-0 text-[10px] text-gray-500">
                    {dashboard.windows.length} tool{dashboard.windows.length === 1 ? '' : 's'}
                  </span>
                </button>
              ))}
            </>
          )}

          {closedDashboards.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-500">
              All saved layouts are already open. Close a tab to shelve one without deleting it.
            </p>
          )}
        </div>
      </PortalDropdown>

      <PortalDropdown
        anchorRef={manageRef}
        isOpen={manageOpen}
        onClose={() => {
          if (editingId !== null) commitRename();
          setManageOpen(false);
        }}
        width={300}
        align="left"
      >
        <div className="py-1 max-h-96 overflow-y-auto">
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[1.5px] text-gray-500">
            Manage layouts
          </div>

          {openDashboards.length > 0 && (
            <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-[1.5px] text-gray-600">
              Open
            </div>
          )}
          {openDashboards.map((dashboard) => (
            <ManageRow
              key={dashboard.id}
              dashboard={dashboard}
              isCurrent={dashboard.id === currentDashboardId}
              editingId={editingId}
              draft={draft}
              setDraft={setDraft}
              commitRename={commitRename}
              cancelRename={() => setEditingId(null)}
              onSelect={() => {
                switchDashboard(dashboard.id);
                setManageOpen(false);
              }}
              onRename={() => beginRename(dashboard.id, dashboard.name)}
              onDefault={() => setDefaultDashboard(dashboard.id)}
              onClose={canCloseTab ? () => closeDashboard(dashboard.id) : undefined}
              onDelete={canDeleteSaved ? () => {
                setConfirmId(dashboard.id);
                setManageOpen(false);
              } : undefined}
            />
          ))}

          {closedDashboards.length > 0 && (
            <div className="px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-[1.5px] text-gray-600 border-t border-dark-border mt-1">
              Saved
            </div>
          )}
          {closedDashboards.map((dashboard) => (
            <ManageRow
              key={dashboard.id}
              dashboard={dashboard}
              isCurrent={false}
              editingId={editingId}
              draft={draft}
              setDraft={setDraft}
              commitRename={commitRename}
              cancelRename={() => setEditingId(null)}
              onSelect={() => handleOpenSaved(dashboard.id)}
              onRename={() => beginRename(dashboard.id, dashboard.name)}
              onDefault={() => setDefaultDashboard(dashboard.id)}
              onOpen={() => handleOpenSaved(dashboard.id)}
              onDelete={canDeleteSaved ? () => {
                setConfirmId(dashboard.id);
                setManageOpen(false);
              } : undefined}
            />
          ))}

          <button
            onClick={handleCreate}
            disabled={!canCreate}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-dark-surface-light transition-colors border-t border-dark-border mt-1 disabled:opacity-40 disabled:hover:text-gray-400 disabled:hover:bg-transparent"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {canCreate ? 'New layout' : `Maximum of ${MAX_DASHBOARDS} saved`}
          </button>
        </div>
      </PortalDropdown>

      {menu && menuDashboard &&
        createPortal(
          <div
            role="menu"
            className="fixed z-[10000] min-w-[168px] py-1 bg-dark-surface border border-dark-border rounded-lg shadow-lg"
            style={{ top: menu.y, left: menu.x }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              role="menuitem"
              onClick={() => handleRenameFromMenu(menuDashboard.id, menuDashboard.name)}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-dark-surface-light"
            >
              Rename
            </button>
            <button
              role="menuitem"
              onClick={() => {
                setDefaultDashboard(menuDashboard.id);
                setMenu(null);
              }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-dark-surface-light"
            >
              {menuDashboard.isDefault ? 'Default layout' : 'Set as default'}
            </button>
            {canCloseTab && (
              <button
                role="menuitem"
                onClick={() => {
                  closeDashboard(menuDashboard.id);
                  setMenu(null);
                }}
                className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-dark-surface-light"
              >
                Close tab
              </button>
            )}
          </div>,
          document.body
        )}

      {pendingDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setConfirmId(null)}
        >
          <div
            className="bg-dark-surface border border-dark-border rounded-lg w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 bg-chrome border-b border-dark-border">
              <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
                Delete Layout
              </h2>
            </div>

            <div className="px-5 py-4">
              <p className="text-sm text-gray-300">
                Permanently delete <span className="font-semibold text-white">{pendingDelete.name}</span>?
              </p>
              <p className="text-xs text-gray-500 mt-1.5">
                Closing a tab keeps the layout. Delete removes its {pendingDelete.windows.length} tool
                {pendingDelete.windows.length === 1 ? '' : 's'} for good.
              </p>
            </div>

            <div className="px-5 py-3 bg-chrome border-t border-dark-border flex justify-end gap-2">
              <button
                onClick={() => setConfirmId(null)}
                className="px-3 py-1.5 text-xs text-gray-300 bg-dark-bg hover:bg-dark-surface-light border border-dark-border rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteDashboard(pendingDelete.id);
                  setConfirmId(null);
                }}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-bear-red hover:bg-bear-red/80 rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface ManageRowProps {
  dashboard: { id: number; name: string; isDefault: boolean };
  isCurrent: boolean;
  editingId: number | null;
  draft: string;
  setDraft: (value: string) => void;
  commitRename: () => void;
  cancelRename: () => void;
  onSelect: () => void;
  onRename: () => void;
  onDefault: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  onDelete?: () => void;
}

const ManageRow: React.FC<ManageRowProps> = ({
  dashboard,
  isCurrent,
  editingId,
  draft,
  setDraft,
  commitRename,
  cancelRename,
  onSelect,
  onRename,
  onDefault,
  onOpen,
  onClose,
  onDelete,
}) => (
  <div
    className={`flex items-center gap-1.5 px-3 py-1.5 hover:bg-dark-surface-light transition-colors ${
      isCurrent ? 'bg-dark-surface-light' : ''
    }`}
  >
    <button
      onClick={onDefault}
      className="flex-shrink-0"
      title={dashboard.isDefault ? 'Default layout' : 'Set as default'}
      aria-label={dashboard.isDefault ? 'Default layout' : `Set ${dashboard.name} as default`}
    >
      {dashboard.isDefault ? (
        <svg className="w-3.5 h-3.5 text-accent-yellow" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5 text-gray-600 hover:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      )}
    </button>

    {editingId === dashboard.id ? (
      <input
        autoFocus
        value={draft}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitRename}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitRename();
          if (e.key === 'Escape') cancelRename();
        }}
        className="flex-1 min-w-0 px-1.5 py-0.5 text-xs bg-dark-bg border border-accent-yellow rounded text-white focus:outline-none"
      />
    ) : (
      <button
        onClick={onSelect}
        className="flex-1 min-w-0 text-left text-sm text-gray-100 truncate"
      >
        {dashboard.name}
      </button>
    )}

    {editingId !== dashboard.id && (
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button
          onClick={onRename}
          className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-dark-bg"
          title="Rename"
          aria-label={`Rename ${dashboard.name}`}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        {onOpen && (
          <button
            onClick={onOpen}
            className="px-1.5 h-6 flex items-center justify-center rounded text-[11px] text-gray-400 hover:text-white hover:bg-dark-bg"
            title="Open as tab"
            aria-label={`Open ${dashboard.name}`}
          >
            Open
          </button>
        )}
        {onClose && (
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-dark-bg"
            title="Close tab"
            aria-label={`Close ${dashboard.name}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
        {onDelete && (
          <button
            onClick={onDelete}
            className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-bear-red hover:bg-bear-red/15"
            title="Delete permanently"
            aria-label={`Delete ${dashboard.name}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    )}
  </div>
);
